import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm, get } from "react-hook-form";
import "./style/finalizeNoLoginStyle.scss";
import "../../../style/feature.scss";
import "../../../style/typography.scss";
import InvoiceButtons from "./InvoiceButtons";
import { FormElementModel } from "../../models/FormElementModel";
import { invoiceForm, userPayForm } from "./inputsForm/InputPayFormModels";
import BasketSummary from "./BasketSummary";
import { useSelector } from "react-redux";
import { State } from "../../store";
import { PaymentLogos } from "../../../features/PaymentLogo";
import Timer from "./Timer";
import { useTranslation } from "react-i18next";
import ReactPixel from "react-facebook-pixel";
import useFacility from "../../../utils/hooks/useFacility";
import RecipientAndPurchaserForm from "./RecipientAndPurchaserForm";
import { FinalizeRecipient } from "../../models/FinalizeRecipient";
import ConsentInputs from "./ConsentInputs/ConsentInputs";
import { gaEvent } from "../../../utils/Analitics/ga";
import { GA_BEGIN_CHECKOUT } from "../../../utils/Analitics/gaEventsConsts";
import { canSendToFacebookPixel, canSendToGA } from "../../../utils/Analitics/analiticsConfigByFacility";
import { sendBeginCheckoutEvent, convertBasketToBeginCheckoutFormat } from "../../../utils/Analitics/GtmEvents";
import { BasketItem } from "../../models/BasketItem";
import SubmitButton from "./SubmitButton";
import TTPayCheckoutNotice from "./TTPayCheckoutNotice";
import EATextField from "../shared/TextField/EATextField";
import EANipInput from "../shared/NipInput/EANipInput";
import EAPhoneInput from "../shared/PhoneInput/EAPhoneInput";
import { useCountryCodes } from "./hooks/useCountryCodes";
import useNewsletterConfirmation from "../NewsletterConfirmation/useNewsletterConfirmation";

export type InputsFinalize = {
  firstName: string;
  lastName: string;
  email: string;
  repeatEmail: string;
  phone: string;
  city: string;
  street: string;
  houseNumber: string;
  flatNumber: string;
  zipCode: string;
  NIP: string;
  companyName: string;
  createUser: boolean;
  createInvoice: boolean;
  acceptEAktywni: boolean;
  acceptPrivacyPolicy: boolean;
  acceptStatute: boolean;
  allowMailing?: boolean;
  nipCountryCode?: string;
  recipient?: FinalizeRecipient;
  purchaser?: FinalizeRecipient;
};

interface IProps {
  onPayTransaction: (value: InputsFinalize) => void;
  onSaveReservation: (value: InputsFinalize) => void;
  alert: string;
  total: number;
  paymentUrl: string;
  loader: boolean;
  basket: BasketItem[];
  promoMessage: string;
  currentPromoCode: string;
  submitButtonTitle?: string;
}

const FinalizeNoLogin: React.FC<IProps> = ({
  onPayTransaction,
  onSaveReservation,
  alert,
  total,
  paymentUrl,
  loader,
  basket,
  promoMessage,
  currentPromoCode,
  submitButtonTitle,
}: IProps) => {
  const saleConfiguration = useSelector((state: State) => state.config.configuration);
  const { t } = useTranslation();

  const [invoiceType, setInvoiceType] = useState<
    "noInvoice" | "customerInvoice" | "companyInvoice" | "recipientAndPurchaserInvoice"
  >("noInvoice");
  const {
    register,
    watch,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
  } = useForm<InputsFinalize>({});
  const zipCodeRequired = useSelector((state: State) => state.config.configuration.zipCodeRequired);
  const phoneNumberRequired = useSelector((state: State) => state.config.configuration.phoneNumberRequired);
  const facilityIds = useFacility();
  const { countryCodes, hasError: countryCodesError } = useCountryCodes();
  const { sendInvite } = useNewsletterConfirmation();
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const isFreeOrder = total <= 0;
  const showInvoiceOptions = saleConfiguration.invoiceActive && !isFreeOrder;

  useEffect(() => {
    if (isFreeOrder) {
      setInvoiceType("noInvoice");
    }
  }, [isFreeOrder]);
  const handleRegistration: SubmitHandler<InputsFinalize> = async (data) => {
    // Czyszczenie pól faktury firmowej gdy nie wybrano faktury na firmę
    const cleanedData: InputsFinalize = {
      ...data,
      NIP: invoiceType === "companyInvoice" ? data.NIP : "",
      companyName: invoiceType === "companyInvoice" ? data.companyName : "",
      nipCountryCode: invoiceType === "companyInvoice" ? data.nipCountryCode : undefined,
    };

    // Zapisujemy stan koszyka lokalnie przed wykonaniem akcji
    const basketSnapshot = [...basket];
    const totalSnapshot = total;
    const promoCodeSnapshot = currentPromoCode;

    analiticsBeginCheckout(basketSnapshot, totalSnapshot, promoCodeSnapshot);

    // Guard to prevent multiple submissions, particularly for free orders (paymentUrl === "")
    setHasSubmitted(true);

    // Zapis do newslettera MUSI zdążyć przed saveReservation/acceptTransaction — te po
    // sukcesie natychmiast przekierowują całą stronę (parent) do bramki płatności, co
    // niszczy dokument i ucina wszystkie w locie/oczekujące requesty. Stąd await tutaj,
    // zamiast odpalać to niezależnie z opóźnieniem gdzieś obok. Ale musi to być best-effort
    // — sendInvite używa "gołego" post() bez obsługi błędów, więc jego wyjątek (np. sieć,
    // nietypowa odpowiedź serwera) nie może ubić całego checkoutu.
    if (data.allowMailing && data.email) {
      try {
        await sendInvite(facilityIds, data.email);
      } catch (e) {
        // Ignorujemy — zapis do newslettera nie może zablokować zakupu.
      }
    }

    if (isFreeOrder) {
      onPayTransaction({ ...cleanedData, createInvoice: false });
    } else if (invoiceType === "recipientAndPurchaserInvoice") {
      onSaveReservation({ ...cleanedData, createInvoice: true });
    } else {
      onPayTransaction({ ...cleanedData, createInvoice: invoiceType !== "noInvoice" });
    }
    // pixelAddBasket();
  };

  const analiticsBeginCheckout = (
    basketSnapshot: any[],
    totalSnapshot: number,
    promoCodeSnapshot: string | undefined
  ) => {
    if (canSendToFacebookPixel(facilityIds)) {
      ReactPixel.fbq("track", "basketValueBeforePayU", {
        value: totalSnapshot,
        currency: "PLN",
      });
    }
    if (canSendToGA(facilityIds)) {
      gaEvent(
        facilityIds,
        "...",
        GA_BEGIN_CHECKOUT,
        {
          value: totalSnapshot,
          currency: "PLN",
        },
        ""
      );
    }

    // Wysyłanie GTM begin_checkout event
    try {
      const promoCode = promoCodeSnapshot && promoCodeSnapshot.length > 0 ? promoCodeSnapshot : undefined;
      const beginCheckoutData = convertBasketToBeginCheckoutFormat(basketSnapshot, totalSnapshot, promoCode);
      sendBeginCheckoutEvent(facilityIds, beginCheckoutData);
    } catch (error) {
      console.error("Error sending GTM begin_checkout event:", error);
    }
  };

  return (
    <div>
      <form className="finalize-no-login flex-between" onSubmit={handleSubmit(handleRegistration)}>
        <div className="basket-box shadow border">
          <h3>{t("dane_zamawiajacego")}</h3>

          {userPayForm(watch, register, zipCodeRequired).map(
            (item: FormElementModel, index: number) => {
              // When invoice is active, skip zipCode here — it's captured in the invoice form
              if (!item.name || (item.name === "zipCode" && showInvoiceOptions && invoiceType !== "noInvoice")) return null;
              return (
                <EATextField
                  key={index}
                  label={item.text}
                  name={item.name}
                  type={item.type as "text" | "email" | "password" | "number"}
                  placeholder={item.placeholder}
                  register={item.register}
                  error={!!get(errors, item.name)}
                  helperText={get(errors, item.name)?.message}
                  fullWidth
                  inputMaxLength={item.inputMaxLength}
                />
              );
            }
          )}

          {phoneNumberRequired && (
            <EAPhoneInput
              control={control}
              name="phone"
              errors={errors}
              label={t("numer_telefonu")}
              required
              fullWidth
            />
          )}

          {showInvoiceOptions && (
            <>
              <h4>{t("faktura")}</h4>
              <InvoiceButtons invoiceType={invoiceType} setInvoiceType={setInvoiceType} />
            </>
          )}
        </div>

        <div className="basket-box shadow border">
          {showInvoiceOptions && invoiceType !== "noInvoice" && (
            <div>
              {invoiceType === "recipientAndPurchaserInvoice" ? (
                <RecipientAndPurchaserForm register={register} errors={errors} control={control} setValue={setValue} countryCodes={countryCodes} countryCodesError={countryCodesError} />
              ) : (
                <>
                  <h3>{t("dane_do_faktury")}</h3>
                  <div className={"inputs-customer-invoice"}>
                    {invoiceForm(register, invoiceType === "companyInvoice").map(
                      (item: FormElementModel, index: number) => (
                        <React.Fragment key={index}>
                          {item.name === "street" && invoiceType === "companyInvoice" && (
                            <EANipInput
                              control={control}
                              name="NIP"
                              countryCodeFieldName="nipCountryCode"
                              setValue={setValue}
                              errors={errors}
                              label={t("nip")}
                              countries={countryCodes}
                              showCountrySelect={!countryCodesError}
                              fullWidth
                            />
                          )}
                          {item.name && (
                            <EATextField
                              label={item.text}
                              name={item.name}
                              type={item.type as "text" | "email" | "password" | "number"}
                              placeholder={item.placeholder}
                              register={item.register}
                              error={!!get(errors, item.name)}
                              helperText={get(errors, item.name)?.message}
                              fullWidth
                              inputMaxLength={item.inputMaxLength}
                            />
                          )}
                        </React.Fragment>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {(isFreeOrder || invoiceType === "noInvoice") && (
            <ConsentInputs
              errors={errors}
              control={control}
              setValue={setValue}
              title={t("zgody")}
              newsletter={true}
            />
          )}
        </div>

        <div className="basket-box shadow border">
          {showInvoiceOptions && invoiceType !== "noInvoice" && (
            <ConsentInputs
              errors={errors}
              control={control}
              setValue={setValue}
              title={t("zgody")}
              newsletter={true}
            />
          )}

          <BasketSummary total={total} />

          {alert.length > 2 && <div className="alert alert-red">{alert}</div>}

          <SubmitButton
            loader={loader}
            paymentUrl={paymentUrl}
            hasSubmitted={hasSubmitted}
            submitButtonTitle={submitButtonTitle}
          />
          <Timer />

          <PaymentLogos />
          <TTPayCheckoutNotice />
        </div>
      </form>
    </div>
  );
};

export default FinalizeNoLogin;
